/**
 * The aim reticle — what you are pointing at, and how far the thing you are
 * about to press actually reaches.
 *
 * This is the single biggest readability idea taken from Brawl Stars, and it is
 * not the dot: it is that YOUR OWN range is drawn on the ground BEFORE you
 * commit. The 3rd-person build drew telegraphs for the people being attacked
 * and nothing at all for the attacker, so judging whether a 2.6-unit cone would
 * connect was guesswork performed on a character 8% of the screen tall.
 *
 * Two pieces, both on the ground plane where distances are honest:
 *   - a ring at the caster's feet showing the reach of the highlighted ability
 *   - a small mark where the aim ray meets the floor
 *
 * Drawn with TSL node materials because the renderer is WebGPU; a legacy
 * material here throws `Uniform "null" not declared` at the first frame.
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS } from '@chaos/protocol';
import { abilityReach } from '@chaos/bot';
import { session, localPlayer } from '../state.js';
import { DECAL_LIFT } from './decals.js';


let _scene = null;
let _ring = null;
let _dot = null;
let _ringRadius = -1;

export function initAimReticle(scene) {
  _scene = scene;

  // A flat annulus rather than a filled disc: a filled circle under your feet
  // hides the thing you are standing on, and the edge is the part that carries
  // the information.
  const ringGeo = new THREE.RingGeometry(0.94, 1, 64);
  ringGeo.rotateX(-Math.PI / 2);
  const ringMat = new THREE.MeshBasicNodeMaterial({ color: 0x7fe8ff });
  ringMat.transparent = true;
  ringMat.opacity = 0.5;
  ringMat.depthWrite = false;
  _ring = new THREE.Mesh(ringGeo, ringMat);
  _ring.frustumCulled = false;
  _ring.renderOrder = 3;
  _scene.add(_ring);

  const dotGeo = new THREE.CircleGeometry(0.35, 24);
  dotGeo.rotateX(-Math.PI / 2);
  const dotMat = new THREE.MeshBasicNodeMaterial({ color: 0xffffff });
  dotMat.transparent = true;
  dotMat.opacity = 0.85;
  dotMat.depthWrite = false;
  _dot = new THREE.Mesh(dotGeo, dotMat);
  _dot.frustumCulled = false;
  _dot.renderOrder = 3;
  _scene.add(_dot);
}

/**
 * @param {THREE.Vector3} aimPoint where the aim ray met the ground
 * @param {string|null} slot ability slot being highlighted, or null for the basic
 * @param {number} groundY
 */
export function updateAimReticle(aimPoint, slot, groundY) {
  if (!_ring || !_dot) return;
  const alive = localPlayer.phase === 'alive';
  _ring.visible = alive;
  _dot.visible = alive;
  if (!alive) return;

  const def = CHARACTERS[session.characterId];
  const ability = def ? def.abilities[slot ?? 'basic'] : null;
  // `abilityReach` is the same function the bots ask "can I hit them?" with, so
  // the ring promises exactly what the simulation will resolve — a second copy
  // of the reach table here is how the drawn range and the real one drift.
  const reach = ability ? Math.max(abilityReach(ability), 1) : 1;

  if (reach !== _ringRadius) {
    _ringRadius = reach;
    _ring.scale.setScalar(reach);
  }
  const p = localPlayer.renderPos;
  _ring.position.set(p.x, groundY + DECAL_LIFT, p.z);
  _dot.position.set(aimPoint.x, groundY + DECAL_LIFT, aimPoint.z);
}

export function disposeAimReticle() {
  for (const m of [_ring, _dot]) {
    if (!m) continue;
    m.removeFromParent();
    m.geometry.dispose();
    m.material.dispose();
  }
  _ring = null;
  _dot = null;
  _ringRadius = -1;
}
